import {
    centsToFormattedDollars,
    formattedDollarsToCents,
} from '@fruitful/fruitils';
import {
    BillActivationStatus,
    BillCategory,
    BillCategoryNames,
    BillingCadence,
} from '@fruitful/types';
import capitalize from 'lodash/capitalize';
import React, { useCallback, useEffect, useState } from 'react';
import {
    DollarInput,
    H6Emphasized,
    Input,
    LabelRegular,
    MultilineInput,
    Picker,
    RowList,
    ScrollView,
    Trash2Icon,
    useToastController,
    XStack,
    YStack,
} from 'smoothie';

import { useRejectBillAndRefetch } from '../../bills/useRejectBill';
import { useUpsertBillAndRefetch } from '../../bills/useUpsertBill';
import { Button } from '../../common/components/GroveButtons';
import { GroveSimpleModal } from '../../common/components/GroveSimpleModal';
import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../common/components/Toasts';
import type { Bill } from '../../entities/entityTypes';

const cadenceLabel = (c: BillingCadence): string =>
    capitalize(c.toLowerCase().split('_').join(' '));

const parseBillCategory = (raw: string | undefined): BillCategory => {
    if (
        raw != null &&
        Object.values(BillCategory).includes(raw as BillCategory)
    ) {
        return raw as BillCategory;
    }
    return BillCategory.OTHER;
};

const firstNonEmptyTrimmed = (
    ...values: Array<string | null | undefined>
): string => {
    for (const value of values) {
        const trimmed = value?.trim();
        if (trimmed != null && trimmed !== '') {
            return trimmed;
        }
    }
    return '';
};

const billToDraft = (bill: Bill) => ({
    displayName: firstNonEmptyTrimmed(bill.displayName, bill.name, bill.payee),
    payee: bill.payee?.trim() ?? '',
    amountStr: centsToFormattedDollars(bill.amount, true),
    cadence: bill.cadence,
    category: parseBillCategory(bill.category),
    notes: bill.description?.trim() ?? '',
});

export type MoneyMapperBillDetailsDraft = ReturnType<typeof billToDraft>;

const categoryPickerOptions = Object.values(BillCategory).map((value) => ({
    value,
    label: BillCategoryNames[value],
}));

const cadencePickerOptions = Object.values(BillingCadence)
    .filter((c) => c !== BillingCadence.YEARLY)
    .map((value) => ({
        value,
        label: cadenceLabel(value),
    }));

export interface MoneyMapperBillDetailsModalProps {
    bill: Bill | null;
    isOpen: boolean;
    membershipId: string;
    onClose: () => void;
}

export const MoneyMapperBillDetailsModal = ({
    bill,
    isOpen,
    membershipId,
    onClose,
}: MoneyMapperBillDetailsModalProps) => {
    const toast = useToastController();
    const { upsertBillAsync } = useUpsertBillAndRefetch(membershipId);
    const { rejectBillAsync } = useRejectBillAndRefetch(membershipId);

    const [draft, setDraft] = useState<MoneyMapperBillDetailsDraft | null>(
        null,
    );
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (bill == null) {
            setDraft(null);
            return;
        }
        setDraft(billToDraft(bill));
    }, [bill?.id, bill]);

    const detailsDisabled = isSubmitting || bill == null || draft == null;

    const persist = useCallback(
        async (nextActivationStatus: BillActivationStatus) => {
            if (bill == null || draft == null) {
                return;
            }

            const name = draft.displayName.trim();
            if (name === '') {
                toast.show(
                    'Display name is required',
                    MutationErrorToastOptions('Add a name for this bill.'),
                );
                return;
            }

            const amountCents = formattedDollarsToCents(draft.amountStr);
            if (!Number.isFinite(amountCents) || amountCents <= 0) {
                toast.show(
                    'Invalid amount',
                    MutationErrorToastOptions('Amount must be greater than 0.'),
                );
                return;
            }
            setIsSubmitting(true);
            try {
                await upsertBillAsync({
                    id: bill.id,
                    membershipId: bill.membershipId,
                    displayName: name,
                    payee: draft.payee.trim() || name,
                    amount: amountCents,
                    accountId: bill.accountId,
                    category: draft.category,
                    cadence: draft.cadence,
                    activationStatus: nextActivationStatus,
                    ...(draft.notes.trim() !== ''
                        ? { description: draft.notes.trim() }
                        : {}),
                });
                toast.show('Bill saved', MutationSuccessToastOptions);
                onClose();
            } catch {
                toast.show(
                    'Could not save bill',
                    MutationErrorToastOptions(
                        'Something went wrong. Try again.',
                    ),
                );
            } finally {
                setIsSubmitting(false);
            }
        },
        [bill, draft, onClose, toast, upsertBillAsync],
    );

    const handleSave = useCallback(async () => {
        await persist(BillActivationStatus.ACTIVE);
    }, [persist]);

    const handleDelete = useCallback(async () => {
        if (bill == null) {
            return;
        }
        setIsSubmitting(true);
        try {
            await rejectBillAsync(bill.id);
            toast.show('Bill removed', MutationSuccessToastOptions);
            onClose();
        } catch {
            toast.show(
                'Could not remove bill',
                MutationErrorToastOptions('Something went wrong. Try again.'),
            );
        } finally {
            setIsSubmitting(false);
        }
    }, [bill, onClose, rejectBillAsync, toast]);

    return (
        <GroveSimpleModal isOpen={isOpen} hasCloseButton onClose={onClose}>
            <YStack
                minWidth={320}
                maxWidth={600}
                maxHeight="78vh"
                flex={1}
                overflow="hidden"
            >
                <YStack
                    flexShrink={0}
                    paddingHorizontal={24}
                    paddingTop={20}
                    paddingBottom={8}
                >
                    <H6Emphasized color="$gray1200">Details</H6Emphasized>
                </YStack>
                {bill != null && draft != null && (
                    <>
                        <ScrollView flex={1} minHeight={0}>
                            <YStack
                                gap={24}
                                paddingHorizontal={24}
                                paddingBottom={16}
                            >
                                <RowList
                                    rowHeaderProps={{ title: 'Details' }}
                                    width="100%"
                                >
                                    <XStack
                                        gap={16}
                                        paddingHorizontal={16}
                                        paddingVertical={8}
                                        flexWrap="wrap"
                                    >
                                        <YStack flex={1} minWidth={200} gap={8}>
                                            <LabelRegular color="$gray1000">
                                                Nickname
                                            </LabelRegular>
                                            <Input
                                                size="regular"
                                                value={draft.displayName}
                                                onChangeText={(t) => {
                                                    setDraft((d) =>
                                                        d != null
                                                            ? {
                                                                  ...d,
                                                                  displayName:
                                                                      t,
                                                              }
                                                            : d,
                                                    );
                                                }}
                                                placeholder="Nickname"
                                                disabled={detailsDisabled}
                                            />
                                        </YStack>
                                        <YStack flex={1} minWidth={200} gap={8}>
                                            <LabelRegular color="$gray1000">
                                                Payee
                                            </LabelRegular>
                                            <Input
                                                size="regular"
                                                value={draft.payee}
                                                onChangeText={(t) => {
                                                    setDraft((d) =>
                                                        d != null
                                                            ? { ...d, payee: t }
                                                            : d,
                                                    );
                                                }}
                                                placeholder="Payee"
                                                disabled={detailsDisabled}
                                            />
                                        </YStack>
                                    </XStack>
                                    <XStack
                                        gap={16}
                                        paddingHorizontal={16}
                                        paddingVertical={8}
                                        flexWrap="wrap"
                                    >
                                        <YStack flex={1} minWidth={200} gap={8}>
                                            <LabelRegular color="$gray1000">
                                                Amount
                                            </LabelRegular>
                                            <DollarInput
                                                size="regular"
                                                value={draft.amountStr}
                                                onChangeText={(t) => {
                                                    setDraft((d) =>
                                                        d != null
                                                            ? {
                                                                  ...d,
                                                                  amountStr: t,
                                                              }
                                                            : d,
                                                    );
                                                }}
                                                allowDecimals
                                                disabled={detailsDisabled}
                                            />
                                        </YStack>
                                        <YStack flex={1} minWidth={200} gap={8}>
                                            <LabelRegular color="$gray1000">
                                                Frequency
                                            </LabelRegular>
                                            <Picker
                                                size="regular"
                                                value={draft.cadence}
                                                options={cadencePickerOptions}
                                                disabled={detailsDisabled}
                                                onChange={(v) => {
                                                    setDraft((d) =>
                                                        d != null
                                                            ? {
                                                                  ...d,
                                                                  cadence: v,
                                                              }
                                                            : d,
                                                    );
                                                }}
                                            />
                                        </YStack>
                                    </XStack>
                                    <XStack
                                        gap={16}
                                        paddingHorizontal={16}
                                        paddingVertical={8}
                                        flexWrap="wrap"
                                    >
                                        <YStack flex={1} minWidth={200} gap={8}>
                                            <LabelRegular color="$gray1000">
                                                Category
                                            </LabelRegular>
                                            <Picker
                                                size="regular"
                                                value={draft.category}
                                                options={categoryPickerOptions}
                                                disabled={detailsDisabled}
                                                onChange={(v) => {
                                                    setDraft((d) =>
                                                        d != null
                                                            ? {
                                                                  ...d,
                                                                  category: v,
                                                              }
                                                            : d,
                                                    );
                                                }}
                                            />
                                        </YStack>
                                    </XStack>
                                    <YStack gap={8} padding={16}>
                                        <LabelRegular color="$gray1000">
                                            Note
                                        </LabelRegular>
                                        <MultilineInput
                                            value={draft.notes}
                                            onChangeText={(t) => {
                                                setDraft((d) =>
                                                    d != null
                                                        ? { ...d, notes: t }
                                                        : d,
                                                );
                                            }}
                                            placeholder="Add your note here"
                                            numberOfLines={4}
                                            disabled={detailsDisabled}
                                        />
                                    </YStack>
                                </RowList>
                            </YStack>
                        </ScrollView>
                        <XStack
                            justifyContent="space-between"
                            alignItems="center"
                            paddingHorizontal={24}
                            paddingVertical={16}
                            paddingTop={12}
                            borderTopWidth={1}
                            borderTopColor="$gray200"
                            flexShrink={0}
                        >
                            <Button
                                variant="tertiary"
                                size="small"
                                tone="danger"
                                disabled={isSubmitting}
                                icon={Trash2Icon}
                                onPress={() => {
                                    void handleDelete();
                                }}
                            >
                                Delete
                            </Button>
                            <Button
                                variant="primary"
                                size="small"
                                disabled={detailsDisabled}
                                onPress={() => {
                                    void handleSave();
                                }}
                            >
                                Save
                            </Button>
                        </XStack>
                    </>
                )}
            </YStack>
        </GroveSimpleModal>
    );
};
